/* eslint-disable prettier/prettier */
import React, { useState } from 'react';

import { Formik, FormikValues, Form } from 'formik';
import * as yup from 'yup';

import Header from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Field from 'components/Field';
import Input from 'components/Input';
import ModalHeader from 'components/ModalHeader';
import { Mobile, Desktop } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { changePassword } from 'services/userService';
import { htmlT } from 'utils/generic';

import { Container, StyledButton, WarningContainer } from './styles';

interface Props {
  show: boolean;
  onClose: () => void;
}

const ChangePasswordForm = ({ show, onClose }: Props) => {
  const { t } = useStoreTranslation();
  const toast = useToast();
  const [loading, setIsLoading] = useState<boolean>(false);

  const validationSchema = yup.object().shape({
    fieldUsername: yup.string().trim().required(),
    fieldCurrentPassword: yup.string(),
    newPassword: yup
      .string()
      .trim()
      .min(8)
      .max(12)
      .required()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[¡,.!@#\$%\^&\*\/()-_+={}[]|\':;¿?<>])(?=.{8,12})/,
        t('changePassword.regex'),
      ),
    confirmNewPassword: yup
      .string()
      .trim()
      .required()
      .oneOf([yup.ref('newPassword'), null], t('changePassword.errors.passwordDoesNotMatch')),
  });

  const initValues = {
    fieldUsername: '',
    fieldCurrentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };

  const handleOnSubmit = async (req) => {
    setIsLoading(true);
    try {
      const { data } = await changePassword(req.fieldUsername, req.fieldCurrentPassword, req.newPassword, 'CO');
      setIsLoading(false);

      onClose();

      toast.add({
        type: 'success',
        message: t('changePassword.successMessage'),
      });
    } catch (errors) {
      let message = t('changePassword.errors.generic');

      if (errors.toString().indexOf('400') !== -1) {
        message = t('changePassword.regex');
      }

      if (errors.toString().indexOf('404') !== -1) {
        message = t('changePassword.errors.invalidUserAndPass');
      }

      toast.add({
        type: 'danger',
        message: message,
      });

      setIsLoading(false);
    }
  };

  return (
    <ApplicationOverlayContent width={700} sidebar show={show} onClose={onClose}>
      <Mobile>
        <Header noGoBack rightIcon="/corporate/images/close.svg" rightAction={onClose}>
          {t('changePassword.title')}
        </Header>
      </Mobile>

      <Desktop>
        <ModalHeader title={t('changePassword.title')} onClose={onClose} />
      </Desktop>

      <Container>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          onSubmit={(req) => {
            handleOnSubmit(req);
          }}
          isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
        >
          {({ isValid }: FormikValues) => (
            <Form autoComplete="off">
              <Field name="fieldUsername" label={t('changePassword.username')}>
                <Input type="text" autoComplete="xxxx" tabIndex={1} />
              </Field>
              <Field name="fieldCurrentPassword" label={t('changePassword.currentPassword')}>
                <Input type="password" autoComplete="new-password" tabIndex={2} />
              </Field>
              <Field name="newPassword" label={t('changePassword.newPassword')}>
                <Input type="password" autoComplete="new-password" tabIndex={3} />
              </Field>
              <Field name="confirmNewPassword" label={t('changePassword.confirmNewPassword')}>
                <Input type="password" autoComplete="new-password" tabIndex={4} />
              </Field>
              <WarningContainer>
                <img src="/corporate/images/payment/addcard-warning.svg" alt="" />
                <span>{htmlT(t, 'changePassword.explanation')}</span>
              </WarningContainer>
              <StyledButton loading={loading} disabled={!isValid || loading} value={t('changePassword.buttonValue')} />
            </Form>
          )}
        </Formik>
      </Container>
    </ApplicationOverlayContent>
  );
};

export default ChangePasswordForm;
